//
/* GS://PROTOCOLO
 * Arquivo: src/pages/CashierReportPage.js
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 1.2.0 – FIX: Isolamento Total de Impressão (Style Injection)
 * Impacto da Versão: [src/pages/CashierReportPage.js]
 * Data: 21/12/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - ADICIONADO: Bloco <style> interno para controle absoluto de visibilidade na impressão (CSS Injection).
 * - AJUSTADO: Container de impressão recebeu ID "printable-report" para ser o único elemento visível.
 * - LÓGICA: @media print { body * { visibility: hidden; } #printable-report { visibility: visible; position: absolute; ... } }
 */

import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase/config';
import { collection, query, orderBy, getDocs, where } from 'firebase/firestore';
import { Loader2, DollarSign, Calendar, User, Info, CheckCircle, XCircle, ShoppingBag, Tag, Printer } from 'lucide-react';

const CashierReportPage = ({ openModal }) => {
    const [cashierSessions, setCashierSessions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedSession, setSelectedSession] = useState(null);
    const [sessionSales, setSessionSales] = useState([]);
    const [isSalesLoading, setIsSalesLoading] = useState(false);

    const fetchCashierSessions = useCallback(async () => {
        setIsLoading(true);
        try {
            const q = query(collection(db, "cashier_sessions"), orderBy("openedAt", "desc"));
            const querySnapshot = await getDocs(q);
            const sessions = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                openedAt: doc.data().openedAt?.toDate(), 
                closedAt: doc.data().closedAt?.toDate(), 
            }));
            setCashierSessions(sessions);
        } catch (error) {
            console.error("Erro ao carregar sessões de caixa:", error);
            openModal(<p>Não foi possível carregar as sessões de caixa. Verifique sua conexão.</p>, "Erro de Carregamento");
        } finally {
            setIsLoading(false);
        }
    }, [openModal]);

    const fetchSessionSales = useCallback(async (sessionId) => {
        setIsSalesLoading(true);
        try {
            const q = query(collection(db, "sales"), where("sessionId", "==", sessionId), orderBy("createdAt", "asc"));
            const querySnapshot = await getDocs(q);
            const sales = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate()
            }));
            setSessionSales(sales);
        } catch (error) {
            console.error("Erro ao carregar vendas da sessão:", error);
            openModal(<p>Não foi possível carregar as vendas para esta sessão. Verifique sua conexão.</p>, "Erro de Vendas");
            setSessionSales([]);
        } finally {
            setIsSalesLoading(false);
        }
    }, [openModal]);

    useEffect(() => {
        fetchCashierSessions();
    }, [fetchCashierSessions]);

    useEffect(() => {
        if (selectedSession) {
            fetchSessionSales(selectedSession.id);
        } else {
            setSessionSales([]);
        }
    }, [selectedSession, fetchSessionSales]);

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Intl.DateTimeFormat('pt-BR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };

    const calculateBalance = (session) => {
        const netCashMovements = (session.cashMovements || []).reduce((sum, move) => sum + (move.amount || 0), 0);
        let cashFromSales = 0;
        if (session.paymentSummaries) {
            if (session.paymentSummaries["Dinheiro"]) {
                cashFromSales += session.paymentSummaries["Dinheiro"];
            }
            if (session.paymentSummaries["Pix"]) {
                cashFromSales += session.paymentSummaries["Pix"];
            }
        }
        return netCashMovements + cashFromSales;
    };

    const onPrint = () => {
        window.print();
    };

    const renderSessionDetails = (session) => {
        const balance = calculateBalance(session);

        return (
            <div className="bg-brand-dark p-6 rounded-lg shadow-inner mt-4">
                <div className="flex justify-between items-center mb-4 border-b border-gray-700 pb-2">
                    <h3 className="text-2xl font-bold text-brand-gold">Detalhes da Sessão</h3>
                    <button
                        onClick={onPrint}
                        className="flex items-center gap-2 bg-white text-gray-900 px-4 py-2 rounded font-bold hover:bg-gray-200 transition-colors"
                    >
                        <Printer size={18} /> Imprimir Relatório
                    </button>
                </div>
                
                {/* Resumo da Sessão */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    <div className="bg-gray-800 p-3 rounded-md">
                        <p className="text-gray-400 text-sm">Operador:</p>
                        <p className="font-semibold text-lg">{session.openedBy?.name || 'Desconhecido'}</p>
                    </div>
                    <div className="bg-gray-800 p-3 rounded-md">
                        <p className="text-gray-400 text-sm">Abertura:</p>
                        <p className="font-semibold text-lg">{formatDate(session.openedAt)}</p>
                    </div>
                    <div className="bg-gray-800 p-3 rounded-md">
                        <p className="text-gray-400 text-sm">Fechamento:</p>
                        <p className="font-semibold text-lg">{session.status === 'closed' ? formatDate(session.closedAt) : 'Sessão Aberta'}</p>
                    </div>
                    <div className="bg-gray-800 p-3 rounded-md">
                        <p className="text-gray-400 text-sm">Status:</p>
                        <p className={`font-semibold text-lg ${session.status === 'open' ? 'text-green-400' : 'text-red-400'}`}>
                            {session.status === 'open' ? <CheckCircle size={18} className="inline mr-2" /> : <XCircle size={18} className="inline mr-2" />}
                            {session.status === 'open' ? 'Aberta' : 'Fechada'}
                        </p>
                    </div>
                    <div className="bg-gray-800 p-3 rounded-md">
                        <p className="text-gray-400 text-sm">Valor Inicial (Registrado):</p>
                        <p className="font-semibold text-lg">{formatCurrency(session.initialAmount || 0)}</p>
                    </div>
                    <div className="bg-gray-800 p-3 rounded-md">
                        <p className="text-gray-400 text-sm">Total de Vendas (Bruto):</p>
                        <p className="font-semibold text-lg">{formatCurrency(session.totalSalesValue || 0)} ({session.salesCount || 0} vendas)</p>
                    </div>
                    <div className="bg-gray-800 p-3 rounded-md">
                        <p className="text-gray-400 text-sm">Saldo Final Calculado (Caixa Físico):</p>
                        <p className="font-semibold text-xl text-green-300">{formatCurrency(balance)}</p>
                    </div>
                </div>

                {/* Resumo de Pagamentos */}
                <h4 className="text-xl font-bold text-gray-300 mb-3 mt-6 border-b border-gray-800 pb-1">Resumo de Pagamentos (Total por Método)</h4>
                {session.paymentSummaries && Object.keys(session.paymentSummaries).length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                        {Object.entries(session.paymentSummaries).map(([method, amount]) => (
                            <div key={method} className="bg-gray-800 p-3 rounded-md flex justify-between items-center">
                                <span className="text-gray-400">{method}:</span>
                                <span className="font-semibold text-lg">{formatCurrency(amount || 0)}</span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-gray-500 text-center py-4">Nenhum registro de pagamentos para esta sessão.</p>
                )}

                {/* Movimentações de Caixa */}
                <h4 className="text-xl font-bold text-gray-300 mb-3 mt-6 border-b border-gray-800 pb-1">Movimentações de Caixa (Suprimentos/Sangrias/Despesas)</h4>
                {session.cashMovements && session.cashMovements.length > 0 ? (
                    <div className="space-y-3 mb-6">
                        {session.cashMovements.map((move, index) => (
                            <div key={index} className={`p-3 rounded-md flex items-center justify-between ${move.amount > 0 ? 'bg-blue-900' : 'bg-red-900'}`}>
                                <div className="flex-1">
                                    <p className="font-semibold text-base">
                                        {move.type === 'initial_supply' ? 'Suprimento Inicial' : move.type === 'supply' ? 'Suprimento' : move.type === 'withdrawal' ? 'Sangria' : 'Despesa'}
                                        <span className="text-gray-300 text-sm ml-2">({move.description})</span>
                                    </p>
                                    <p className="text-gray-400 text-sm">Registrado por: {move.registeredBy?.name || 'N/A'} em {formatDate(move.timestamp?.toDate())}</p>
                                </div>
                                <span className={`font-bold text-lg ${move.amount > 0 ? 'text-green-300' : 'text-red-300'}`}>
                                    {formatCurrency(move.amount || 0)}
                                </span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-gray-500 text-center py-4 mb-6">Nenhuma movimentação de caixa adicional registrada nesta sessão.</p>
                )}

                {/* Detalhes das Vendas da Sessão */}
                <h4 className="text-xl font-bold text-gray-300 mb-3 mt-6 border-b border-gray-800 pb-1">Vendas Realizadas na Sessão</h4>
                {isSalesLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-brand-gold" />
                        <span className="ml-3 text-lg text-gray-400">Carregando vendas...</span>
                    </div>
                ) : sessionSales.length > 0 ? (
                    <div className="space-y-4">
                        {sessionSales.map(sale => (
                            <div key={sale.id} className="bg-gray-800 p-4 rounded-md shadow-sm">
                                <div className="flex justify-between items-center mb-2 border-b border-gray-700 pb-2">
                                    <p className="font-bold text-brand-gold flex items-center">
                                        <ShoppingBag size={18} className="mr-2" /> Venda ID: {sale.id.substring(0, 6)}
                                    </p>
                                    <span className="text-sm text-gray-400">{formatDate(sale.createdAt)}</span>
                                </div>
                                <p className="text-gray-300 mb-2">Cliente: {sale.customerName}</p>
                                <p className="text-gray-300 mb-2">Método de Pagamento: {sale.paymentMethod || 'N/A'}</p>
                                <p className="text-gray-300 mb-2">Total da Venda: {formatCurrency(sale.total || 0)}</p>
                                {sale.discount > 0 && <p className="text-gray-300 mb-2">Desconto Aplicado: {formatCurrency(sale.discount)} ({sale.pointsUsed} pontos)</p>}
                                
                                <h5 className="text-md font-semibold text-gray-400 mt-3 mb-2">Itens Vendidos:</h5>
                                <ul className="list-disc list-inside text-gray-300 ml-4 space-y-1">
                                    {sale.items && sale.items.length > 0 ? (
                                        sale.items.map((item, itemIndex) => (
                                            <li key={itemIndex} className="flex items-center">
                                                <Tag size={14} className="mr-2 text-brand-gold" />
                                                {item.name} (x{item.quantity}) - {formatCurrency(item.price)} cada
                                            </li>
                                        ))
                                    ) : (
                                        <li>Nenhum item registrado para esta venda.</li>
                                    )}
                                </ul>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-gray-500 text-center py-4">Nenhuma venda registrada para esta sessão.</p>
                )}

                {/* Botão de Fechar Detalhes */}
                <button
                    onClick={() => setSelectedSession(null)}
                    className="mt-8 w-full p-3 bg-gray-700 text-white font-bold rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center"
                >
                    <XCircle size={20} className="mr-2" /> Fechar Detalhes
                </button>
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="flex h-full w-full items-center justify-center bg-brand-black text-white">
                <Loader2 className="h-12 w-12 animate-spin text-brand-gold" />
                <span className="ml-4 text-xl">Carregando relatórios de caixa...</span>
            </div>
        );
    }

    return (
        <>
            {/* [GS-FIX-PRINT] Solução Definitiva: CSS Injection 
               Força a ocultação de TUDO no body e exibe APENAS o #printable-report.
            */}
            <style>
                {`
                    @media print {
                        body * {
                            visibility: hidden;
                        }
                        #printable-report, #printable-report * {
                            visibility: visible;
                        }
                        #printable-report {
                            position: absolute;
                            left: 0;
                            top: 0;
                            width: 100%;
                            margin: 0;
                            padding: 20px;
                            background: white;
                            color: black;
                            z-index: 99999;
                        }
                        /* Ocultar elementos específicos que possam sobrar */
                        nav, aside, header, footer {
                            display: none !important;
                        }
                    }
                `}
            </style>

            {/* LAYOUT DE TELA (UI PRINCIPAL) */}
            <div className="p-6 bg-gray-900 min-h-screen text-white">
                <h1 className="text-4xl font-extrabold text-brand-gold mb-8 border-b-4 border-brand-gold pb-2">
                    <DollarSign size={36} className="inline mr-3 -mt-1" />
                    Gestão de Caixa e Relatórios
                </h1>

                {selectedSession ? (
                    renderSessionDetails(selectedSession)
                ) : (
                    <div className="grid grid-cols-1 gap-6">
                        {cashierSessions.length === 0 ? (
                            <div className="bg-brand-dark p-8 rounded-lg text-center text-gray-400">
                                <Info size={48} className="mx-auto mb-4" />
                                <p className="text-xl">Nenhuma sessão de caixa encontrada.</p>
                                <p className="text-md">Abra um caixa no PDV para começar a registrar sessões.</p>
                            </div>
                        ) : (
                            cashierSessions.map(session => (
                                <div 
                                    key={session.id} 
                                    className="bg-brand-dark p-6 rounded-lg shadow-md cursor-pointer hover:bg-gray-800 transition-colors"
                                    onClick={() => setSelectedSession(session)}
                                >
                                    <div className="flex justify-between items-center mb-3">
                                        <h2 className="text-xl font-bold text-brand-gold flex items-center">
                                            <Calendar size={20} className="mr-2" /> Sessão de {formatDate(session.openedAt)}
                                        </h2>
                                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                                            session.status === 'open' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                                        }`}>
                                            {session.status === 'open' ? 'Aberta' : 'Fechada'}
                                        </span>
                                    </div>
                                    <p className="text-gray-300 mb-2 flex items-center">
                                        <User size={16} className="mr-2" /> Operador: {session.openedBy?.name || 'Desconhecido'}
                                    </p>
                                    <div className="flex justify-between items-center text-lg mt-4 pt-3 border-t border-gray-700">
                                        <span className="text-gray-400">Total de Vendas (Bruto):</span>
                                        <span className="font-bold text-white">{formatCurrency(session.totalSalesValue || 0)}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-lg">
                                        <span className="text-gray-400">Nº de Vendas:</span>
                                        <span className="font-bold text-white">{session.salesCount || 0}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-lg">
                                        <span className="text-gray-400">Saldo Calculado (Caixa Físico):</span>
                                        <span className="font-bold text-green-300">{formatCurrency(calculateBalance(session))}</span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>

            {/* LAYOUT DE IMPRESSÃO (RELATÓRIO FORMAL) */}
            {selectedSession && (
                <div id="printable-report" className="hidden print:block bg-white text-black">
                    {/* Cabeçalho */}
                    <div className="mb-6 border-b-2 border-black pb-4">
                        <div className="flex justify-between items-start">
                            <div>
                                <h1 className="text-3xl font-bold uppercase tracking-wide">Golden Sisters</h1>
                                <h2 className="text-lg text-gray-600 mt-1">Relatório de Fechamento de Caixa</h2>
                            </div>
                            <div className="text-right text-xs text-gray-500">
                                <p>ID Sessão: {selectedSession.id}</p>
                                <p>Gerado em: {new Date().toLocaleString('pt-BR')}</p>
                            </div>
                        </div>
                    </div>

                    {/* Resumo da Sessão */}
                    <div className="mb-6">
                        <h3 className="text-sm font-bold uppercase border-b border-gray-300 mb-3 pb-1">Resumo da Sessão</h3>
                        <div className="grid grid-cols-2 gap-4 text-xs">
                            <div>
                                <p><span className="font-semibold">Operador:</span> {selectedSession.openedBy?.name || 'Desconhecido'}</p>
                                <p><span className="font-semibold">Abertura:</span> {formatDate(selectedSession.openedAt)}</p>
                                <p><span className="font-semibold">Fechamento:</span> {selectedSession.status === 'closed' ? formatDate(selectedSession.closedAt) : 'Em Aberto'}</p>
                            </div>
                            <div className="text-right">
                                <p><span className="font-semibold">Valor Inicial:</span> {formatCurrency(selectedSession.initialAmount || 0)}</p>
                                <p><span className="font-semibold">Vendas (Bruto):</span> {formatCurrency(selectedSession.totalSalesValue || 0)}</p>
                                <p className="text-sm mt-1"><span className="font-bold">Saldo Final (Caixa):</span> {formatCurrency(calculateBalance(selectedSession))}</p>
                            </div>
                        </div>
                    </div>

                    {/* Resumo Financeiro Grid */}
                    <div className="mb-6 p-4 border border-gray-300 bg-gray-50 rounded">
                        <h3 className="text-sm font-bold uppercase mb-3">Totais por Pagamento</h3>
                        {selectedSession.paymentSummaries && Object.keys(selectedSession.paymentSummaries).length > 0 ? (
                            <div className="grid grid-cols-3 gap-2 text-xs">
                                {Object.entries(selectedSession.paymentSummaries).map(([method, amount]) => (
                                    <div key={method} className="flex justify-between border-b border-gray-200 pb-1">
                                        <span>{method}</span>
                                        <span className="font-mono font-bold">{formatCurrency(amount || 0)}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-xs text-gray-500 italic">Sem pagamentos registrados.</p>
                        )}
                    </div>

                    {/* Movimentações */}
                    {selectedSession.cashMovements && selectedSession.cashMovements.length > 0 && (
                        <div className="mb-6">
                            <h3 className="text-sm font-bold uppercase border-b border-gray-300 mb-3 pb-1">Movimentações de Caixa</h3>
                            <table className="w-full text-xs text-left">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <th className="py-1 px-2">Data</th>
                                        <th className="py-1 px-2">Tipo</th>
                                        <th className="py-1 px-2">Descrição/Resp.</th>
                                        <th className="py-1 px-2 text-right">Valor</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedSession.cashMovements.map((move, idx) => (
                                        <tr key={idx} className="border-b border-gray-100">
                                            <td className="py-1 px-2">{formatDate(move.timestamp?.toDate())}</td>
                                            <td className="py-1 px-2 uppercase">{move.type === 'initial_supply' ? 'Inicial' : move.type === 'supply' ? 'Suprimento' : move.type === 'withdrawal' ? 'Sangria' : 'Despesa'}</td>
                                            <td className="py-1 px-2">{move.description} ({move.registeredBy?.name})</td>
                                            <td className={`py-1 px-2 text-right font-bold ${move.amount < 0 ? 'text-red-700' : ''}`}>
                                                {formatCurrency(move.amount)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Vendas Detalhadas */}
                    <div className="mt-4">
                         <h3 className="text-sm font-bold uppercase border-b border-black mb-3 pb-1">Detalhamento de Vendas ({sessionSales.length})</h3>
                         <table className="w-full text-xs text-left border-collapse">
                            <thead>
                                <tr className="border-b border-black">
                                    <th className="py-2 w-20">Hora</th>
                                    <th className="py-2">Cliente / Pagamento</th>
                                    <th className="py-2">Itens</th>
                                    <th className="py-2 text-right w-24">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sessionSales.map((sale, idx) => (
                                    <tr key={sale.id} className={`border-b border-gray-200 align-top ${idx % 2 === 0 ? 'bg-gray-50 print:bg-transparent' : ''}`}>
                                        <td className="py-2 font-mono">
                                            {sale.createdAt ? sale.createdAt.toLocaleTimeString('pt-BR', {hour: '2-digit', minute:'2-digit'}) : '-'}
                                        </td>
                                        <td className="py-2 pr-2">
                                            <div className="font-semibold">{sale.customerName}</div>
                                            <div className="text-[10px] uppercase text-gray-500">{sale.paymentMethod}</div>
                                            <div className="text-[10px] text-gray-400">ID: {sale.id.substring(0,6)}</div>
                                        </td>
                                        <td className="py-2 pr-2">
                                            {sale.items && sale.items.map((item, i) => (
                                                <div key={i} className="flex justify-between text-[10px]">
                                                    <span>{item.quantity}x {item.name}</span>
                                                </div>
                                            ))}
                                        </td>
                                        <td className="py-2 text-right font-bold">
                                            {formatCurrency(sale.total || 0)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                         </table>
                    </div>

                    <div className="mt-8 text-[10px] text-center text-gray-400 border-t border-gray-200 pt-2">
                        <p>Documento de Conferência Interna - Ecossistema GoldenSisters</p>
                    </div>
                </div>
            )}
        </>
    );
};

export default CashierReportPage;